import { StafferPetSprite } from "../StafferPetSprite";
import { Column } from "../../Column";
import type {
  ShopCartItem,
  ShopCatalogEntry,
  ShopCollectionStats,
  ShopItemGroup,
  ShopOutfit,
  ShopSlot,
  ShopSlotItem,
} from "./types";

import { stafferPetShop } from "@/lib/stafferPetShop";
import { stafferPets } from "@/lib/stafferPets";
import type {
  Pet,
  StafferPetPaletteId,
  StafferPetRarity,
  StafferPetSpecies,
} from "@/types";

type TopperId = keyof typeof stafferPets.TOPPERS;
type AccessoryId = keyof typeof stafferPets.ACCESSORIES;

const TILE_SPRITE_SIZE_PX = 64;
const SLOT_SPRITE_SIZE_PX = 44;
// Locked items show their shape but not their look.
const SILHOUETTE_CLASS_NAME = "opacity-40 brightness-0";
// One-off unlocks made for a single person have no shop tier.
const CUSTOM_ITEM_RARITY: StafferPetRarity = "EPIC";
const RESTRICTED_PET_RARITY: StafferPetRarity = "LEGENDARY";

const petOptions = stafferPetShop.sections.PETS.items;
const colorOptions = stafferPetShop.sections.COLORS.items;
const topperOptions = stafferPetShop.sections.TOPPERS.items;
const accessoryOptions = stafferPetShop.sections.ACCESSORIES.items;
// The crown is granted by the server, never picked, so it stays out of the rack.
const topperIds = (Object.keys(stafferPets.TOPPERS) as TopperId[]).filter(
  (id) => id !== stafferPets.CROWN_TOPPER_ID,
);
const accessoryIds = Object.keys(stafferPets.ACCESSORIES) as AccessoryId[];

const slotFields = {
  PET: "petId",
  COLOR: "colorId",
  TOPPER: "topperId",
  ACCESSORY_1: "accessoryId",
  ACCESSORY_2: "accessoryId2",
} as const satisfies Record<ShopSlot, keyof ShopOutfit>;

const slotCopy: Record<ShopSlot, { title: string; description: string }> = {
  PET: {
    title: stafferPetShop.sections.PETS.title,
    description: "Swap anytime · your outfit comes along",
  },
  COLOR: {
    title: stafferPetShop.sections.COLORS.title,
    description: stafferPetShop.sections.COLORS.description,
  },
  TOPPER: {
    title: stafferPetShop.sections.TOPPERS.title,
    description: "Head slot · unlock once, wear on any pet",
  },
  ACCESSORY_1: {
    title: `${stafferPetShop.sections.ACCESSORIES.title} · Accessory 1`,
    description: "Pairs with your topper and Accessory 2",
  },
  ACCESSORY_2: {
    title: `${stafferPetShop.sections.ACCESSORIES.title} · Accessory 2`,
    description: "Pairs with your topper and Accessory 1",
  },
};

// Ledger descriptions read "Unlocked Gold color", "Unlocked Bat pet".
const cartSuffixes: Partial<Record<ShopSlot, string>> = {
  PET: " pet",
  COLOR: " color",
};

/* Ownership */
const isExclusiveItem = (item: object): boolean =>
  ("isExclusive" in item && item.isExclusive === true) ||
  !stafferPetShop.isItemAvailable(item);

const isPetOwned = (pet: Pet, id: StafferPetSpecies): boolean => {
  if (pet.unlocked.petIds.includes(id)) return true;
  const option = stafferPets.AVAILABLE_SPECIES.find((item) => item.id === id);
  const shopItem = petOptions.find((item) => item.id === id);
  return option?.isRestricted !== true && shopItem === undefined;
};

const isColorOwned = (pet: Pet, id: StafferPetPaletteId): boolean =>
  stafferPets.isBasePaletteId(id) || pet.unlocked.colorIds.includes(id);

const isTopperOwned = (pet: Pet, id: string | null): boolean =>
  id === null || pet.unlocked.topperIds.includes(id);

const isAccessoryOwned = (pet: Pet, id: string | null): boolean =>
  id === null || (pet.unlocked.accessoryIds ?? []).includes(id);

const getPaletteId = (id: string | null): StafferPetPaletteId | null =>
  stafferPets.BASE_PALETTE_IDS.find((baseId) => baseId === id) ??
  colorOptions.find((item) => item.id === id)?.id ??
  null;

const isSlotItemOwned = (pet: Pet, slot: ShopSlot, id: string | null) => {
  if (slot === "PET") {
    const option = stafferPets.AVAILABLE_SPECIES.find((item) => item.id === id);
    return option === undefined || isPetOwned(pet, option.id);
  }
  if (slot === "COLOR") {
    const paletteId = getPaletteId(id);
    return paletteId === null || isColorOwned(pet, paletteId);
  }
  if (slot === "TOPPER") return isTopperOwned(pet, id);
  return isAccessoryOwned(pet, id);
};

/* Entry helpers */
const getGroup = (
  isWorn: boolean,
  isOwned: boolean,
  isLocked: boolean,
): ShopItemGroup => {
  if (isWorn) return "WEARING";
  if (isOwned) return "OWNED";
  if (isLocked) return "LOCKED";
  return "FOR_SALE";
};

// Free defaults have no shop item; owned exclusives and retired drops say so.
const getOwnedLabel = (item: object | undefined): string => {
  if (item === undefined) return "Free";
  if (!stafferPetShop.isItemAvailable(item)) return "Retired";
  if ("isExclusive" in item && item.isExclusive === true) return "Exclusive";
  return "Owned";
};

const getLockedNote = (item: object | undefined, fallback: string): string =>
  item !== undefined &&
  "description" in item &&
  typeof item.description === "string"
    ? item.description
    : fallback;

const getPetRarity = (id: StafferPetSpecies): StafferPetRarity => {
  const shopItem = petOptions.find((item) => item.id === id);
  if (shopItem) return stafferPetShop.getItemRarity(shopItem);
  const option = stafferPets.AVAILABLE_SPECIES.find((item) => item.id === id);
  return option?.isRestricted ? RESTRICTED_PET_RARITY : "COMMON";
};

const getTopperRarity = (id: string): StafferPetRarity => {
  const shopItem = topperOptions.find((item) => item.id === id);
  return shopItem ? stafferPetShop.getItemRarity(shopItem) : CUSTOM_ITEM_RARITY;
};

const getAccessoryRarity = (id: string): StafferPetRarity => {
  const shopItem = accessoryOptions.find((item) => item.id === id);
  return shopItem ? stafferPetShop.getItemRarity(shopItem) : CUSTOM_ITEM_RARITY;
};

const getItemImage = (src: string, isLocked: boolean) => (
  <img
    alt=""
    src={src}
    className={`h-3/4 w-3/4 object-contain ${isLocked ? SILHOUETTE_CLASS_NAME : ""}`}
    style={{ imageRendering: "pixelated" }}
  />
);

/* Catalog entries per slot */
const getPetEntries = (
  pet: Pet,
  outfit: ShopOutfit,
  equipped: ShopOutfit,
): ShopCatalogEntry[] =>
  stafferPets.AVAILABLE_SPECIES.map((option) => {
    const shopItem = petOptions.find((item) => item.id === option.id);
    const isOwned = isPetOwned(pet, option.id);
    const isLocked =
      !isOwned && (shopItem === undefined || isExclusiveItem(shopItem));
    return {
      id: option.id,
      label: option.label,
      art: (
        <StafferPetSprite
          species={option.id}
          paletteId={outfit.colorId}
          sizePx={stafferPets.getDisplaySizePx(option.id, TILE_SPRITE_SIZE_PX)}
          hasPaletteGlow={false}
          className={isLocked ? SILHOUETTE_CLASS_NAME : undefined}
        />
      ),
      group: getGroup(equipped.petId === option.id, isOwned, isLocked),
      isSelected: outfit.petId === option.id,
      lockedNote: "Restricted pet",
      ownedLabel: getOwnedLabel(shopItem),
      price: shopItem?.price ?? 0,
      rarity: getPetRarity(option.id),
    };
  });

const getColorEntries = (
  pet: Pet,
  outfit: ShopOutfit,
  equipped: ShopOutfit,
): ShopCatalogEntry[] => {
  const getEntry = (
    id: StafferPetPaletteId,
    item: (typeof colorOptions)[number] | undefined,
  ): ShopCatalogEntry => {
    const isOwned = isColorOwned(pet, id);
    const isLocked =
      !isOwned && item !== undefined && isExclusiveItem(item);
    return {
      id,
      label: stafferPets.PALETTES[id].label,
      art: (
        <StafferPetSprite
          species={outfit.petId}
          paletteId={id}
          sizePx={stafferPets.getDisplaySizePx(
            outfit.petId,
            TILE_SPRITE_SIZE_PX,
          )}
          hasPaletteGlow={false}
          className={isLocked ? SILHOUETTE_CLASS_NAME : undefined}
        />
      ),
      group: getGroup(equipped.colorId === id, isOwned, isLocked),
      isSelected: outfit.colorId === id,
      lockedNote: getLockedNote(item, "Exclusive"),
      ownedLabel: getOwnedLabel(item),
      price: item?.price ?? 0,
      rarity: stafferPets.PALETTES[id].rarity,
    };
  };

  return [
    ...stafferPets.BASE_PALETTE_IDS.map((id) => getEntry(id, undefined)),
    ...colorOptions.map((item) => getEntry(item.id, item)),
  ];
};

const getTopperEntries = (
  pet: Pet,
  outfit: ShopOutfit,
  equipped: ShopOutfit,
): ShopCatalogEntry[] => {
  const getEntry = (
    id: TopperId,
    item: (typeof topperOptions)[number] | undefined,
  ): ShopCatalogEntry => {
    const isOwned = isTopperOwned(pet, id);
    const isLocked = !isOwned && (item === undefined || isExclusiveItem(item));
    return {
      id,
      label: stafferPets.TOPPERS[id].label,
      art: getItemImage(stafferPets.TOPPERS[id].src, isLocked),
      group: getGroup(equipped.topperId === id, isOwned, isLocked),
      isSelected: outfit.topperId === id,
      lockedNote: getLockedNote(item, "Retired"),
      ownedLabel: item ? getOwnedLabel(item) : "Exclusive",
      price: item?.price ?? 0,
      rarity: getTopperRarity(id),
    };
  };
  // Custom toppers are made for one person, so they only show for their owner.
  const customIds = topperIds.filter(
    (id) =>
      isTopperOwned(pet, id) && !topperOptions.some((item) => item.id === id),
  );

  return [
    ...topperOptions.map((item) => getEntry(item.id, item)),
    ...customIds.map((id) => getEntry(id, undefined)),
  ];
};

const getAccessoryEntries = (
  pet: Pet,
  outfit: ShopOutfit,
  equipped: ShopOutfit,
  slot: "ACCESSORY_1" | "ACCESSORY_2",
): ShopCatalogEntry[] => {
  const field = slotFields[slot];
  const getSlotTag = (id: string): string | undefined => {
    if (outfit.accessoryId === id) return "ACC 1";
    if (outfit.accessoryId2 === id) return "ACC 2";
    return undefined;
  };
  const getEntry = (
    id: AccessoryId,
    item: (typeof accessoryOptions)[number] | undefined,
  ): ShopCatalogEntry => {
    const isOwned = isAccessoryOwned(pet, id);
    const isLocked = !isOwned && (item === undefined || isExclusiveItem(item));
    return {
      id,
      label: stafferPets.ACCESSORIES[id].label,
      art: getItemImage(stafferPets.ACCESSORIES[id].src, isLocked),
      group: getGroup(equipped[field] === id, isOwned, isLocked),
      isSelected: outfit[field] === id,
      lockedNote: getLockedNote(item, "Retired"),
      ownedLabel: item ? getOwnedLabel(item) : "Exclusive",
      price: item?.price ?? 0,
      rarity: getAccessoryRarity(id),
      slotTag: getSlotTag(id),
    };
  };
  const customIds = accessoryIds.filter(
    (id) =>
      isAccessoryOwned(pet, id) &&
      !accessoryOptions.some((item) => item.id === id),
  );

  return [
    ...accessoryOptions.map((item) => getEntry(item.id, item)),
    ...customIds.map((id) => getEntry(id, undefined)),
  ];
};

const getOutfit = (pet: Pet): ShopOutfit => ({
  petId: pet.equipped.petId,
  colorId: pet.equipped.colorId,
  topperId:
    pet.equipped.topperId === stafferPets.CROWN_TOPPER_ID
      ? null
      : (pet.equipped.topperId ?? null),
  accessoryId: pet.equipped.accessoryId ?? null,
  accessoryId2: pet.equipped.accessoryId2 ?? null,
});

const getEntries = (
  slot: ShopSlot,
  pet: Pet,
  outfit: ShopOutfit,
): ShopCatalogEntry[] => {
  const equipped = getOutfit(pet);
  if (slot === "PET") return getPetEntries(pet, outfit, equipped);
  if (slot === "COLOR") return getColorEntries(pet, outfit, equipped);
  if (slot === "TOPPER") return getTopperEntries(pet, outfit, equipped);
  return getAccessoryEntries(pet, outfit, equipped, slot);
};

const getSlotItem = (
  outfit: ShopOutfit,
  slot: ShopSlot,
): ShopSlotItem | null => {
  if (slot === "PET") {
    return {
      art: (
        <StafferPetSprite
          species={outfit.petId}
          paletteId={outfit.colorId}
          sizePx={stafferPets.getDisplaySizePx(
            outfit.petId,
            SLOT_SPRITE_SIZE_PX,
          )}
          hasPaletteGlow={false}
        />
      ),
      label: stafferPets.getSpeciesLabel(outfit.petId),
      price: petOptions.find((item) => item.id === outfit.petId)?.price ?? 0,
      rarity: getPetRarity(outfit.petId),
    };
  }
  if (slot === "COLOR") {
    const palette = stafferPets.PALETTES[outfit.colorId];
    return {
      art: (
        <Column
          className="block h-10 w-10 border-2 border-slate-900"
          style={{
            background: stafferPets.getPaletteSwatchBackground(outfit.colorId),
          }}
        />
      ),
      label: palette.label,
      price:
        colorOptions.find((item) => item.id === outfit.colorId)?.price ?? 0,
      rarity: palette.rarity,
    };
  }
  if (slot === "TOPPER") {
    const topper = stafferPets.getTopper(outfit.topperId);
    if (topper === null || outfit.topperId === null) return null;
    return {
      art: getItemImage(topper.src, false),
      label: topper.label,
      price:
        topperOptions.find((item) => item.id === outfit.topperId)?.price ?? 0,
      rarity: getTopperRarity(outfit.topperId),
    };
  }
  const id = outfit[slotFields[slot]];
  const accessory = stafferPets.getAccessory(id);
  if (accessory === null || id === null) return null;
  return {
    art: getItemImage(accessory.src, false),
    label: accessory.label,
    price: accessoryOptions.find((item) => item.id === id)?.price ?? 0,
    rarity: getAccessoryRarity(id),
  };
};

// Swaps one slot; an accessory can only sit in one accessory slot at a time.
const applyItem = (
  outfit: ShopOutfit,
  slot: ShopSlot,
  id: string | null,
): ShopOutfit => {
  if (slot === "PET") {
    const option = stafferPets.AVAILABLE_SPECIES.find((item) => item.id === id);
    return option ? { ...outfit, petId: option.id } : outfit;
  }
  if (slot === "COLOR") {
    const paletteId = getPaletteId(id);
    return paletteId ? { ...outfit, colorId: paletteId } : outfit;
  }
  if (slot === "TOPPER") return { ...outfit, topperId: id };
  if (slot === "ACCESSORY_1") {
    return {
      ...outfit,
      accessoryId: id,
      accessoryId2:
        id !== null && outfit.accessoryId2 === id ? null : outfit.accessoryId2,
    };
  }
  return {
    ...outfit,
    accessoryId:
      id !== null && outfit.accessoryId === id ? null : outfit.accessoryId,
    accessoryId2: id,
  };
};

const getCartItems = (pet: Pet, outfit: ShopOutfit): ShopCartItem[] =>
  (Object.keys(slotFields) as ShopSlot[]).flatMap((slot) => {
    const id = outfit[slotFields[slot]];
    if (id === null || isSlotItemOwned(pet, slot, id)) return [];
    const item = getSlotItem(outfit, slot);
    if (item === null) return [];
    const description = `${item.label}${cartSuffixes[slot] ?? ""}`;
    return [{ slot, id, description, price: item.price }];
  });

const getChangedSlots = (pet: Pet, outfit: ShopOutfit): ShopSlot[] => {
  const equipped = getOutfit(pet);
  return (Object.keys(slotFields) as ShopSlot[]).filter(
    (slot) => outfit[slotFields[slot]] !== equipped[slotFields[slot]],
  );
};

const pickRandom = <Item,>(items: readonly Item[]): Item =>
  items[Math.floor(Math.random() * items.length)];

// Only draws from owned items, so a shuffle never puts anything in the cart.
// The pet itself stays put; shuffle only restyles it.
const getShuffledOutfit = (pet: Pet, outfit: ShopOutfit): ShopOutfit => {
  const ownedColors = [
    ...stafferPets.BASE_PALETTE_IDS,
    ...colorOptions
      .filter((item) => isColorOwned(pet, item.id))
      .map((item) => item.id),
  ];
  const ownedToppers = [
    null,
    ...topperIds.filter((id) => isTopperOwned(pet, id)),
  ];
  const ownedAccessories = [
    null,
    ...accessoryIds.filter((id) => isAccessoryOwned(pet, id)),
  ];
  const accessoryId = pickRandom(ownedAccessories);
  const accessoryId2 = pickRandom(
    ownedAccessories.filter((id) => id === null || id !== accessoryId),
  );

  return {
    petId: outfit.petId,
    colorId: pickRandom(ownedColors),
    topperId: pickRandom(ownedToppers),
    accessoryId,
    accessoryId2,
  };
};

const getCollectionStats = (pet: Pet): ShopCollectionStats => {
  const outfit = getOutfit(pet);
  const entries = [
    ...getPetEntries(pet, outfit, outfit),
    ...getColorEntries(pet, outfit, outfit),
    ...getTopperEntries(pet, outfit, outfit),
    ...getAccessoryEntries(pet, outfit, outfit, "ACCESSORY_1"),
  ];
  const owned = entries.filter(
    (entry) => entry.group === "WEARING" || entry.group === "OWNED",
  );

  return {
    collected: owned.length,
    exclusive: owned.filter(
      (entry) =>
        entry.ownedLabel === "Exclusive" || entry.ownedLabel === "Retired",
    ).length,
    legendary: owned.filter((entry) => entry.rarity === "LEGENDARY").length,
    // Locked items are someone else's customs or retired, so they don't count.
    total: entries.filter((entry) => entry.group !== "LOCKED").length,
  };
};

export const shopCatalog = {
  applyItem,
  getCartItems,
  getChangedSlots,
  getCollectionStats,
  getEntries,
  getOutfit,
  getShuffledOutfit,
  getSlotItem,
  slotCopy,
  slotFields,
};
